'use strict';

(() => {
  // Sets up a global object to hold custom functions and classes.
  window.mx = window.mx || {};

  /**
   * Sets the CSS variable --scrollbar-width to the width of the scrollbar.
   */
  const setScrollBarWidthProperty = () => {
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`);
  };
  window.addEventListener('resize', setScrollBarWidthProperty);
  setScrollBarWidthProperty();

  /**
   * Returns whether the provided element is currently locked.
   * @param {HTMLElement} [element=document.documentElement] - The element to check.
   * @returns {boolean} - Whether the element is locked.
   */
  window.mx.isScrollLocked = (element = document.documentElement) => {
    return element.hasAttribute('data-scroll-lock');
  };

  /**
   * Locks scrolling on the provided element and saves the current scroll position.
   * @param {HTMLElement} [element=document.documentElement] - The element to lock scrolling on.
   */
  window.mx.lockScroll = (element = document.documentElement) => {
    element.style.scrollBehavior = 'auto';
    element.style.top = `-${window.scrollY}px`;
    element.setAttribute('data-scroll-lock', '');
  };

  /**
   * Unlocks scrolling on the provided element and restores the previous scroll position.
   * @param {HTMLElement} [element=document.documentElement] - The element to unlock scrolling on.
   */
  window.mx.unlockScroll = (element = document.documentElement) => {
    element.removeAttribute('data-scroll-lock');
    window.scrollTo(0, -1 * parseInt(element.style.top));
    element.style.top = null;
    element.style.scrollBehavior = null;
  };

  /**
   * Manages event listeners for easy addition and removal.
   */
  window.mx.EventManager = class {
    constructor() {
      this._listeners = new Map();
    }

    /**
     * Adds an event listener and manages it for easy removal.
     * @param {EventTarget} element - The element to attach the event listener to.
     * @param {String} type - The type of event to listen for.
     * @param {Function} handler - The function to be called when the event occurs.
     * @param {Object} options - Options for the event listener.
     */
    addListener(element, type, handler, options) {
      if (!(element instanceof EventTarget)) {
        throw new Error('Invalid element provided to addListener');
      }

      element.addEventListener(type, handler, options);

      if (!this._listeners.has(element)) {
        this._listeners.set(element, new Set());
      }
      this._listeners.get(element).add({ type, handler });
    }

    /**
     * Removes a listener for the event.
     */
    removeListener(element, type, handler) {
      element.removeEventListener(type, handler);
    }

    /**
     * Removes all managed event listeners.
     */
    removeAllListeners() {
      for (const [element, listeners] of this._listeners) {
        listeners.forEach(({ type, handler }) => {
          this.removeListener(element, type, handler);
        });
      }
      this._listeners.clear();
    }
  };

  /**
   * Creates a throttled function that only invokes the provided callback function at most once per animation frame.
   * @param {Function} callback - The callback function to be throttled.
   * @returns {Function} - The throttled function.
   */
  window.mx.throttle = (callback) => {
    let requestId = null;
    let lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  };

  /**
   * Creates a debounced function that delays invoking the provided callback function.
   * @param {Function} callback - The callback function to be debounced.
   * @param {number} [delay=100] - The delay in milliseconds before invoking the callback function.
   * @returns {Function} - The debounced function.
   */
  window.mx.debounce = (callback, delay = 100) => {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  };

  window.mx.MxComponent = class extends HTMLElement {
    constructor() {
      super();
      this.eventManager = new window.mx.EventManager();
    }

    connectedCallback() {
      // Use this.eventManager.addListener to add easy cleanup event listeners.
    }

    get section() {
      return this.closest('.shopify-section');
    }

    disconnectedCallback() {
      this.eventManager.removeAllListeners();
    }
  };
  if (!window.customElements.get('mx-component')) {
    window.customElements.define('mx-component', window.mx.MxComponent);
  }

  window.mx.MxSection = class extends window.mx.MxComponent {
    constructor() {
      super();
    }
  };
  if (!window.customElements.get('mx-section')) {
    window.customElements.define('mx-section', window.mx.MxSection);
  }

  window.mx.MxDrawer = class extends window.mx.MxSection {
    constructor() {
      super();
    }

    get overlay() {
      return this.section.querySelector('.mx-overlay');
    }

    open() {
      this.section.setAttribute('data-open', '');
      window.mx.lockScroll();
    }
    close() {
      this.section.removeAttribute('data-open');
      window.mx.unlockScroll();
    }
  };
  if (!window.customElements.get('mx-drawer')) {
    window.customElements.define('mx-drawer', window.mx.MxDrawer);
  }

  window.mx.MxSlider = class extends window.mx.MxComponent {
    constructor() {
      super();

      if (this.slideList.length === 0) return;

      this.cachedConfig = null;
      this.eventHandlerList = [];
      this.pendingScroll = false;
      this.activeIndex = 0;
      this.isDragging = false;
      this.dragPosition = {};
      this.nearingStart = false;
      this.leavingStart = true;
      this.nearingEnd = true;
      this.leavingEnd = false;

      // this.logEvents();
    }

    connectedCallback() {
      this.eventManager.addListener(
        this,
        'scroll',
        window.mx.throttle(() => this.onScroll()),
        { passive: true },
      );

      this.eventManager.addListener(
        this,
        'scroll',
        window.mx.debounce(() => this.onScrollEnd(), 5),
        { passive: true },
      );

      new ResizeObserver(this.onResize).observe(this);

      if (this.allowDrag) {
        const mediaQuery = window.matchMedia('screen and (pointer: fine)');

        this.eventManager.addListener(mediaQuery, 'change', (event) => {
          if (event.matches) {
            this.eventManager.addListener(this, 'pointerdown', (event) => this.onPointerDown(event));
          } else {
            this.eventManager.removeListener(this, 'pointerdown', (event) => this.onPointerDown(event));
          }
        });

        if (mediaQuery.matches) {
          this.eventManager.addListener(this, 'pointerdown', (event) => this.onPointerDown(event));
        }
      } else {
        this.style.setProperty('cursor', 'auto');
      }

      this.activeIndex = Math.max(
        0,
        this.slideList.findIndex((slide) => slide.hasAttribute('data-initial')),
      );

      if (this.activeIndex > 0) {
        this.select(this.activeIndex, 'auto');
      }
    }

    get config() {
      if (this.cachedConfig === null) {
        const configElement = document.querySelector(`#${this.id}__config`);

        try {
          this.cachedConfig = configElement ? JSON.parse(configElement.textContent) : {};
        } catch (error) {
          console.error('Error parsing slider JSON configuration', this, error);
        }
      }

      return this.cachedConfig;
    }
    get slideList() {
      return Array.from(this.children);
    }
    get activeSlide() {
      return this.slideList[this.activeIndex];
    }
    get fullyVisibleSlideCount() {
      return [...this.slideList]
        .map((slide) => {
          const slideLeft = slide.offsetLeft;
          const slideRight = slideLeft + slide.clientWidth;

          return slideRight <= this.scrollLeft + this.clientWidth && slideLeft >= this.scrollLeft;
        })
        .filter((visibleSlide) => visibleSlide).length;
    }
    get allowDrag() {
      return this.config?.allowDrag ?? true;
    }
    get loopOver() {
      return this.config?.loopOver ?? false;
    }
    get adaptiveHeight() {
      return this.config?.adaptiveHeight ?? false;
    }

    logEvents() {
      const eventList = [
        'mx-slider:select',
        'mx-slider:change',
        'mx-slider:edge-nearing',
        'mx-slider:edge-leaving',
        'mx-slider:settle',
      ];

      eventList.forEach((eventName) => {
        this.eventManager.addListener(this, eventName, (event) => {
          console.log(eventName, event.detail);
        });
      });
    }

    previous(behavior) {
      if (this.loopOver && this.leavingStart) {
        this.select(this.slideList.length - this.fullyVisibleSlideCount, behavior);
      } else {
        this.select(Math.max(this.activeIndex - 1, 0), behavior);
      }
    }
    next(behavior) {
      if (this.loopOver && this.leavingEnd) {
        this.select(0, behavior);
      } else {
        this.select(Math.min(this.activeIndex + 1, this.slideList.length - 1), behavior);
      }
    }
    select(index, behavior) {
      if (!(index in this.slideList) || !this.checkVisibility() || this.offsetWidth === 0 || this.offsetHeight === 0)
        return;

      this.dispatchEvent(
        new CustomEvent('mx-slider:select', {
          detail: { index, slide: this.slideList[index] },
        }),
      );

      this.updateActiveIndex(index);
      this.pendingScroll = true;
      this.scrollTo({
        left: this.calculateLeftScroll(this.slideList[index]),
        behavior: behavior,
      });
    }
    updateActiveIndex(index) {
      if (index === this.activeIndex) return;

      this.activeIndex = index;

      this.dispatchEvent(
        new CustomEvent('mx-slider:change', {
          detail: { index: this.activeIndex, slide: this.activeSlide },
        }),
      );
    }
    calculateLeftScroll(slide) {
      const scrollPadding = getComputedStyle(this).scrollPaddingInlineStart;
      const scrollPaddingValue = scrollPadding === 'auto' ? 0 : parseInt(scrollPadding);
      const scrollLeft = slide.offsetLeft - scrollPaddingValue;

      return Math.min(Math.max(scrollLeft, 0), this.scrollWidth - this.clientWidth);
    }
    calculateClosestIndex() {
      const cellOffsetList = this.slideList.map((slide) => slide.offsetLeft);

      return cellOffsetList.indexOf(
        cellOffsetList.reduce((prev, curr) =>
          Math.abs(curr - this.scrollLeft) < Math.abs(prev - this.scrollLeft) ? curr : prev,
        ),
      );
    }
    onScroll() {
      const scrollEdgeThreshold = 100;

      if (this.scrollLeft < scrollEdgeThreshold && this.nearingStart) {
        this.dispatchEvent(new CustomEvent('mx-slider:edge-nearing', { detail: 'start' }));
        this.nearingStart = false;
        this.leavingStart = true;
      }

      if (this.scrollLeft >= scrollEdgeThreshold && this.leavingStart) {
        this.dispatchEvent(new CustomEvent('mx-slider:edge-leaving', { detail: 'start' }));
        this.nearingStart = true;
        this.leavingStart = false;
      }

      if (this.scrollWidth - this.clientWidth < this.scrollLeft + scrollEdgeThreshold && this.nearingEnd) {
        this.dispatchEvent(new CustomEvent('mx-slider:edge-nearing', { detail: 'end' }));
        this.nearingEnd = false;
        this.leavingEnd = true;
      }

      if (this.scrollWidth - this.clientWidth >= this.scrollLeft + scrollEdgeThreshold && this.leavingEnd) {
        this.dispatchEvent(new CustomEvent('mx-slider:edge-leaving', { detail: 'end' }));
        this.leavingEnd = false;
        this.nearingEnd = true;
      }
    }
    onScrollEnd() {
      this.pendingScroll = false;

      if (this.isDragging || this.activeIndex < this.slideList.length - this.fullyVisibleSlideCount) {
        this.updateActiveIndex(this.calculateClosestIndex());
      }

      this.dispatchEvent(
        new CustomEvent('mx-slider:settle', {
          detail: { index: this.activeIndex, slide: this.activeSlide },
        }),
      );
    }
    onPointerDown(event) {
      this.dragPosition = {
        left: this.scrollLeft,
        top: this.scrollTop,
        x: event.clientX,
        y: event.clientY,
      };

      this.isDragging = true;

      this.eventManager.addListener(document, 'pointermove', (event) => this.onPointerMove(event));
      this.eventManager.addListener(document, 'pointerup', () => this.onPointerUp());
      this.eventManager.addListener(this, 'click', (event) => this.onClick(event), {
        once: true,
      });
    }
    onPointerMove = (event) => {
      if (!this.isDragging) return;

      event.preventDefault();

      const [dx, dy] = [
        event.clientX - this.dragPosition.x,
        event.clientY - this.dragPosition.y,
      ];

      this.scrollTop = this.dragPosition.top - dy;
      this.scrollLeft = this.dragPosition.left - dx;
    };
    onClick(event) {
      if (event.clientX - this.dragPosition.x !== 0) {
        event.preventDefault();
      }
    }
    onPointerUp = () => {
      this.isDragging = false;

      if (!this.pendingScroll) {
        this.scrollTo({
          left: this.calculateLeftScroll(this.activeSlide),
          behavior: 'smooth',
        });
      }

      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
    };
    onResize() {
      if (this.activeIndex !== this.calculateClosestIndex) {
        this.select(this.activeIndex, 'smooth');
      }
    }
  };
  if (!window.customElements.get('mx-slider')) {
    window.customElements.define('mx-slider', window.mx.MxSlider);
  }

  window.mx.MxSliderNavigation = class extends window.mx.MxComponent {
    constructor() {
      super();

      this.cachedSlider = null;
    }

    connectedCallback() {
      if (!this.slider) {
        throw new Error(
          'Slider navigation component requires an aria-controls attribute that refers to the controlled slider.',
        );
      }

      this.navigationItemList.forEach((navigationItem, index) => {
        this.eventManager.addListener(navigationItem, 'click', () => this.onClick(index));
      });

      this.eventManager.addListener(this.slider, 'mx-slider:change', (event) =>
        this.onNavigationChange(event.detail.index),
      );
    }

    disconnectedCallback() {
      this.eventManager.removeAllListeners();
    }

    get slider() {
      if (this.cachedSlider === null) {
        const sliderElement = document.getElementById(this.getAttribute('aria-controls'));
        if (!sliderElement) return null;

        this.cachedSlider = sliderElement;
      }

      return this.cachedSlider;
    }
    get navigationItemList() {
      return Array.from(this.children);
    }

    onClick(index) {
      this.slider.select(index, 'smooth');
      this.onNavigationChange(index);
    }
    onNavigationChange(newIndex) {
      this.navigationItemList.forEach((navigationItem, index) => {
        navigationItem.setAttribute('aria-current', index === newIndex ? 'true' : 'false');
      });

      if (this.scrollWidth !== this.clientWidth) {
        this.scrollTo({
          left:
            this.navigationItemList[newIndex].offsetLeft -
            this.clientWidth / 2 +
            this.navigationItemList[newIndex].clientWidth / 2,
          behavior: 'smooth',
        });
      } else if (this.scrollHeight !== this.clientHeight) {
        this.scrollTo({
          top:
            this.navigationItemList[newIndex].offsetTop -
            this.clientHeight / 2 +
            this.navigationItemList[newIndex].clientHeight / 2,
          behavior: 'smooth',
        });
      }
    }
  };
  if (!window.customElements.get('mx-slider-navigation')) {
    window.customElements.define('mx-slider-navigation', window.mx.MxSliderNavigation);
  }

  window.mx.MxSliderNavigationButton = class extends window.mx.MxComponent {
    constructor() {
      super();

      this.cachedSlider = null;
    }

    connectedCallback() {
      if (!this.slider) {
        throw new Error(
          'Slider navigation button component requires an aria-controls attribute that refers to the controlled slider.',
        );
      } else if (!this.direction) {
        throw new Error('Slider navigation button component requires either a data-next or data-previous attribute.');
      }

      this.eventManager.addListener(this, 'click', () => this.onClick());

      if (!this.loopOver) {
        this.eventManager.addListener(this.slider, 'mx-slider:edge-nearing', (event) => this.onEdgeNearing(event));
        this.eventManager.addListener(this.slider, 'mx-slider:edge-leaving', (event) => this.onEdgeLeaving(event));

        if (
          (this.direction === 'previous' && this.slider.activeIndex === 0) ||
          (this.direction === 'next' &&
            this.slider.activeIndex >= this.slider.slideList.length - this.slider.fullyVisibleSlideCount)
        )
          this.setAttribute('disabled', '');
      }
    }

    get slider() {
      if (this.cachedSlider === null) {
        const sliderElement = document.getElementById(this.getAttribute('aria-controls'));
        if (!sliderElement) return null;

        this.cachedSlider = sliderElement;
      }

      return this.cachedSlider;
    }
    get direction() {
      if (!this.hasAttribute('data-next') && !this.hasAttribute('data-previous')) return null;

      return this.hasAttribute('data-next') ? 'next' : 'previous';
    }
    get loopOver() {
      return this.slider?.config?.loopOver ?? false;
    }

    onClick() {
      if (this.direction === 'next') {
        this.slider.next('smooth');
      } else {
        this.slider.previous('smooth');
      }
    }
    onEdgeNearing(event) {
      if (this.direction === 'previous' && event.detail === 'start') {
        this.setAttribute('disabled', '');
      } else if (this.direction === 'previous' && event.detail === 'end') {
        this.removeAttribute('disabled');
      } else if (this.direction === 'next' && event.detail === 'start') {
        this.removeAttribute('disabled');
      } else if (this.direction === 'next' && event.detail === 'end') {
        this.setAttribute('disabled', '');
      }
    }
    onEdgeLeaving(event) {
      if (this.direction === 'previous' && event.detail === 'start') {
        this.removeAttribute('disabled');
      } else if (this.direction === 'next' && event.detail === 'end') {
        this.removeAttribute('disabled');
      }
    }
  };
  if (!window.customElements.get('mx-slider-navigation-button')) {
    window.customElements.define('mx-slider-navigation-button', window.mx.MxSliderNavigationButton);
  }

  window.mx.MxSafeSticky = class extends window.mx.MxComponent {
    constructor() {
      super();

      this.position = 'relative';
      this.initialTop = 0;
      this.currentTop = 0;
      this.lastKnownY = window.scrollY;
    }

    connectedCallback() {
      this.eventManager.addListener(
        window,
        'scroll',
        window.mx.throttle(() => this.onScroll()),
        { passive: true },
      );
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this);
      this.onResize();
    }

    disconnectedCallback() {
      this.eventManager.removeAllListeners();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    onScroll() {
      this.setPositionOffset();
    }

    onResize() {
      this.style.top = null;

      const { top, position } = getComputedStyle(this);
      this.initialTop = parseFloat(top) || 0;
      this.position = position;
      this.currentTop = this.initialTop;

      this.setPositionOffset();
    }

    setPositionOffset() {
      if (this.position !== 'sticky') {
        this.style.top = null;
        return;
      }

      const rect = this.getBoundingClientRect();
      const maxTop = rect.top + window.scrollY - this.offsetTop + this.initialTop;
      const minTop = this.clientHeight - window.innerHeight;

      const scrollDifference = window.scrollY - this.lastKnownY;
      this.currentTop -= scrollDifference;

      this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, this.initialTop);
      this.lastKnownY = window.scrollY;

      this.style.top = `${Math.round(this.currentTop)}px`;
    }
  };
  if (!window.customElements.get('mx-safe-sticky')) {
    window.customElements.define('mx-safe-sticky', window.mx.MxSafeSticky);
  }

  window.mx.hideGorgiasButton = () => {
    const gorgiasButton = document.querySelector('#gorgias-chat-container');
    if (!gorgiasButton) return;
  
    gorgiasButton.style.visibility = 'hidden';
    gorgiasButton.style.opacity = 0;
  }

  window.mx.showGorgiasButton = () => {
    const gorgiasButton = document.querySelector('#gorgias-chat-container');
    if (!gorgiasButton) return;
  
    gorgiasButton.style.visibility = null;
    gorgiasButton.style.opacity = null;
  }
})();
